import styles from './CardContent.module.scss';

import { Skeleton } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { get } from 'lodash';
import React, { useEffect, useRef } from 'react';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { DEFAULT_GRID_ROW_HEIGHT_REM } from '../../../grid-layout/GridLayout';
import {
  ICardWidgetColumnDef,
  IGetCellModifiers,
  IGetRowModifiers,
} from '../../types';

import { Cell, Row } from './components';

export interface ICardContentProps<T> {
  columns: ICardWidgetColumnDef<T>[];
  items: T[];
  fetchMore: () => void;
  totalCount: number;
  getItemHref: (item: T) => string;
  isSelected?: (item: T) => boolean;
  onItemClick?: (item: T, event: React.MouseEvent<HTMLElement>) => void;
  onItemDoubleClick?: (item: T, event: React.MouseEvent<HTMLElement>) => void;
  getRowModifiers?: IGetRowModifiers<T>;
  getCellModifiers?: IGetCellModifiers<T>;
}

const MAX_PLACEHOLDER_ITEMS = 3;

/**
 * This is grid where every row has one primary cell and several secondary
 * Primary always takes remaining space (1fr).
 * Secondary takes as much as he needs (auto).
 * The largest cell value determines the width of the whole column.
 */

export function CardContent<T>(props: ICardContentProps<T>) {
  const {
    items,
    totalCount,
    getItemHref,
    isSelected,
    onItemClick,
    onItemDoubleClick,
    getCellModifiers,
    getRowModifiers,
    columns = [],
    fetchMore,
  } = props;

  // Row height takes default GridLayout row height
  const rowHeight = remToPx(DEFAULT_GRID_ROW_HEIGHT_REM);

  const gridTemplateColumns = columns
    .map((column) => (column.primary ? '1fr' : 'auto'))
    .join(' ');

  const bindItemHandler = (
    handler: (item: T, event: React.MouseEvent<HTMLElement>) => void,
    item: T
  ) => {
    if (!handler) {
      return undefined;
    }

    return (event: React.MouseEvent<HTMLElement>) => {
      handler(item, event);
    };
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: Math.min(totalCount, items.length + MAX_PLACEHOLDER_ITEMS),
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan: 0,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Fetch more data when needed
  useEffect(() => {
    virtualItems.forEach((virtualRow) => {
      const index = virtualRow.index;
      const row = items[index];

      if (typeof fetchMore === 'function' && row == null) {
        fetchMore();
      }
    });
  }, [virtualItems, items, fetchMore]);

  // Render the placeholder row with skeleton
  const renderPlaceholderRow = () => {
    return (
      <Row<T> item={null} style={{ gridTemplateColumns }}>
        {columns.map((column) => {
          return (
            <Cell
              key={column.field}
              field={column.field}
              type={column.type}
              primary={column.primary}
              align={column.align}
              value=""
              modifiers={column.modifiers}
              children={<Skeleton variant="text" height="100%" width="100%" />}
            />
          );
        })}
      </Row>
    );
  };

  return (
    <div
      ref={containerRef}
      className={styles.root}
      style={{ gridAutoRows: `${rowHeight}px` }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const index = virtualRow.index;
          const row = items[index];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${rowHeight}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {row == null ? (
                renderPlaceholderRow()
              ) : (
                <Row<T>
                  key={index}
                  item={row}
                  href={getItemHref(row)}
                  selected={isSelected?.(row)}
                  onClick={bindItemHandler(onItemClick, row)}
                  onDoubleClick={bindItemHandler(onItemDoubleClick, row)}
                  modifiers={getRowModifiers?.({ row })}
                  style={{
                    gridTemplateColumns,
                  }}
                >
                  {columns.map((column) => {
                    const cellParams = getCellParams({
                      row,
                      column,
                      getCellModifiers,
                    });

                    return <Cell key={column.field} {...cellParams} />;
                  })}
                </Row>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getCellParams<T>({
  row,
  column,
  getCellModifiers,
}: {
  row: T;
  column: ICardWidgetColumnDef<T>;
  getCellModifiers: IGetCellModifiers<T>;
}) {
  const { field, align, primary, renderCell, type, modifiers } = column;

  const value = get(row, column.field);

  return {
    field,
    type,
    primary,
    align,
    value,
    modifiers: getCellModifiers
      ? getCellModifiers?.({ row, column })
      : modifiers,
    children: renderCell?.({ row, value }),
  };
}
